import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import { Car, Pencil, Plus, Trash2, X } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Skeleton'
import { formatEuros } from '@/lib/format'
import { formatHmm } from '@/lib/hours'
import { useWeekHistory } from '@/lib/time-queries'
import type { WeekHistoryItem } from '@/lib/types'
import { cn } from '@/lib/utils'
import { weekMetaFromId } from '@/lib/week'

interface WeekHistoryDrawerProps {
  weekId: string
  personId?: string
  personName?: string
  open: boolean
  onClose: () => void
}

const FIELD_LABELS: Record<string, string> = {
  hours: 'uren',
  description: 'omschrijving',
  category: 'categorie',
  travelAmount: 'reiskosten',
  amount: 'bedrag',
  expenseDate: 'datum',
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'leeg'
  const text = String(value)
  if (field === 'hours') return formatHmm(Number(text))
  if (['travelAmount', 'amount'].includes(field)) return formatEuros(Number(text))
  return text
}

function actionLabel(item: WeekHistoryItem): string {
  const noun = item.entityType === 'TravelExpense' ? 'reiskosten' : 'uren'
  switch (item.action) {
    case 'created':
      return `boekte ${noun}`
    case 'corrected':
      return `wijzigde ${noun}`
    case 'deleted':
      return `verwijderde ${noun}`
    default:
      return item.action
  }
}

function ActionIcon({ item }: { item: WeekHistoryItem }) {
  const base = 'h-3.5 w-3.5'
  if (item.entityType === 'TravelExpense') return <Car className={cn(base, 'text-ink-faint')} />
  if (item.action === 'created') return <Plus className={cn(base, 'text-emerald-600')} />
  if (item.action === 'deleted') return <Trash2 className={cn(base, 'text-red-500')} />
  return <Pencil className={cn(base, 'text-amber-600')} />
}

function HistoryLine({ item }: { item: WeekHistoryItem }) {
  const date = item.diff.workDate ?? item.diff.expenseDate
  const dayLabel = date ? format(parseISO(date), 'EEEE d MMM', { locale: nl }) : null
  const context = [item.diff.clientName, item.diff.projectName, item.diff.categoryName]
    .filter(Boolean)
    .join(' · ')

  const changes = Object.entries(item.diff.changes ?? {})
  const snapshot = item.action === 'created' ? item.diff.new : item.action === 'deleted' ? item.diff.old : undefined
  const snapshotParts = Object.entries(snapshot ?? {})
    .filter(([field, value]) => value !== null && value !== '' && FIELD_LABELS[field])
    .map(([field, value]) => `${FIELD_LABELS[field]} ${formatValue(field, value)}`)

  return (
    <li className="border-b border-hairline px-4 py-3 last:border-b-0">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fill-1">
          <ActionIcon item={item} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink">
            <span className="font-medium">{item.actorName ?? 'Systeem'}</span> {actionLabel(item)}
            {dayLabel ? <span className="text-ink-soft"> op {dayLabel}</span> : null}
          </p>
          {context ? <p className="truncate text-xs text-ink-faint">{context}</p> : null}
          {changes.length > 0 ? (
            <ul className="mt-1 space-y-0.5">
              {changes
                .filter(([field]) => FIELD_LABELS[field])
                .map(([field, change]) => (
                  <li key={field} className="text-xs text-ink-soft">
                    <span className="text-ink-faint">{FIELD_LABELS[field]}:</span>{' '}
                    <span className="num">{formatValue(field, change.from)}</span>
                    {' → '}
                    <span className="num font-medium text-ink">{formatValue(field, change.to)}</span>
                  </li>
                ))}
            </ul>
          ) : snapshotParts.length > 0 ? (
            <p className="mt-1 text-xs text-ink-soft">{snapshotParts.join(' · ')}</p>
          ) : null}
          <p className="mt-1 text-xs text-ink-faint/80">
            {format(parseISO(item.createdAt), 'd MMM yyyy HH:mm', { locale: nl })}
            {item.channel.toLowerCase() !== 'web' ? (
              <span className="ml-1.5 rounded-pill bg-fill-2 px-1.5 py-px text-[10px] uppercase tracking-wide">
                {item.channel.toLowerCase()}
              </span>
            ) : null}
          </p>
        </div>
      </div>
    </li>
  )
}

export function WeekHistoryDrawer({ weekId, personId, personName, open, onClose }: WeekHistoryDrawerProps) {
  const historyQuery = useWeekHistory(weekId, personId, open)
  const meta = weekMetaFromId(weekId)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <button type="button" aria-label="Sluiten" onClick={onClose} className="absolute inset-0 bg-black/25" />
      <aside className="absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-surface shadow-pop">
        <header className="flex items-start justify-between border-b border-hairline px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">Historie {meta.label.toLowerCase()}</h2>
            <p className="text-xs text-ink-faint">
              {personName ? `Uren en reiskosten van ${personName}` : 'Uren en reiskosten'} · {meta.rangeLabel}
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost h-8 w-8 p-0" aria-label="Historie sluiten">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {historyQuery.isError ? (
            <ErrorState compact error={historyQuery.error} onRetry={() => historyQuery.refetch()} />
          ) : historyQuery.isLoading ? (
            <div className="space-y-3 p-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (historyQuery.data?.items.length ?? 0) === 0 ? (
            <div className="p-4">
              <EmptyState title="Nog geen wijzigingen" description="Voor deze week is niets gewijzigd of geboekt." />
            </div>
          ) : (
            <ul>
              {historyQuery.data?.items.map((item) => <HistoryLine key={item.id} item={item} />)}
            </ul>
          )}
        </div>
      </aside>
    </div>
  )
}
